import {useState, useCallback, useRef,useContext, useMemo, useEffect} from 'react';
import {Route, Switch, useHistory, Link, useParams} from "react-router-dom";
import {useQuery, useQueryClient} from "react-query";
import {fetchProcessingOrders} from "../../services";
import { fetchWarehouseShippingMethods } from '../../services/warehouseSevices';
import {useDispatch, useSelector} from "react-redux";
import {UserContext} from "../../hooks/UserContext";
import {
    ActionMenu,
    Button,
    CarrierName,
    ConfirmDialog,
    DialogView,
    ExpandableList,
    FormattedDate, Notification,
    PageTopBar,
    PageView, SlidePanel,
} from "../../components"
import { BasicTable } from "./../../components/BasicTable/BasicTable"
import {PROCESSING_GENERATE_LABEL, ORDERS_PROCESSING, PROCESSING_ORDER_DETAIL, GENERATE_LABELS, GENERATE_ORDER_LABEL, GENERATE_SHARED_LABEL} from "../../navigation/constants"
import { StatusPill } from '../../components/BasicTable/StatusPill'
import { ORDER_STATUSES_PROCESSING } from '../../components/BasicTable/StatusPillDefinitions';
import { Loader } from '../../components/Loader';
import {ShowOrderContainer} from "./ShowOrderContainer";
import { DateTimeFilter, SelectColumnFilter, SelectColumnMultiFilter } from '../../components/BasicTable/modules/ColumnFiltersUI';
import { PickabilityBadge, PICKABILITY_STATUSES } from './PickabilityBadge';
import { pickAllOrderStocks, partialFulfillOrdersInReview, partialFulfillB2b, fetchPartialFulfillmentResult } from '../../services/orderServices';
import { getPartialFulfillErrorCode, getPartialFulfillErrorMessage, PARTIAL_FULFILL_BLOCKING_CODES } from './partialFulfillErrors';
import { getPartialFulfillMode } from './partialFulfillEligibility';
import { canUndoInReview, isOrderInReview, RECOVERABLE_UNDO_ERRORS } from './undoInReviewEligibility';
import { getReviewBlockMessage } from './GenerateShipment/packingReviewBlock';
import { PartialFulfillDialog } from './PartialFulfillDialog';
import { UndoInReviewDialog } from './UndoInReviewDialog';
import { undoPickingOrderInReview } from '../../services/pickingOrderServices';
import { AssignOrdersDialog } from './AssignOrdersDialog';
import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { Tooltip } from 'antd';
import { setProcessingOrdersListPageSize } from '../../redux/pageSizeSlice';
import { FormattedRelativeTime } from '../../components/FormattedRelativeTime';
import { useTranslation } from 'react-i18next';
import { formatShippingMethodFilterId, formatShippingMethodFilterLabel } from '../../utils/orderUtils';
import { fetchStoreConfig } from '../../services';
import { PICKING_PRODUCTIVITIES } from '../../navigation/constants';
import cloneDeep from 'lodash/cloneDeep';
import { PickingTasksSummary } from './PickingTasksSummary';

const WAREHOUSES_WITH_SHARED_LABELS = [100, 133] // hardcoded: show shared-label option for these warehouses
const KEY_PARTIAL_FULFILLMENT_IN_REVIEW_ENABLED = 'partial_fulfillment_in_review_enabled'
const PERMISSION_PARTIALLY_FULFILL_IN_REVIEW = 'partially_fulfill_in_review'


export const ListProcessingOrders = () => {
    const history = useHistory()
    const {user} = useContext(UserContext)
    const {warehouse_id} = useParams()
    const queryClient = useQueryClient()
    const [partialFulfillmentInReviewStoreConfigs, setPartialFulfillmentInReviewStoreConfigs] = useState({})
    const [showPickAllSuccess, setShowPickAllSuccess] = useState(false)
    const [showPickAllConfirmation, setShowPickAllConfirmation] = useState(false)
    const [pickAllStockOrder, setPickAllStockOrder] = useState(null)
    const [loadingPickAllStock, setLoadingPickAllStock] = useState(null)
    const [loadingUndoInReview, setLoadingUndoInReview] = useState(null)
    const [showUndoInReviewErrorDialog, setShowUndoInReviewErrorDialog] = useState(false)
    const [undoInReviewErrorMessage, setUndoInReviewErrorMessage] = useState('')
    const [undoInReviewOrderInfo, setUndoInReviewOrderInfo] = useState({ orderNumber: null, storeName: null })
    const [undoInReviewOrder, setUndoInReviewOrder] = useState(null)
    const [showPartialFulfillDialog, setShowPartialFulfillDialog] = useState(false)
    const [partialFulfillOrder, setPartialFulfillOrder] = useState(null)
    const [partialFulfillState, setPartialFulfillState] = useState('confirmation')
    const [partialFulfillErrorMessage, setPartialFulfillErrorMessage] = useState('')
    const [partialFulfillErrorList, setPartialFulfillErrorList] = useState([])
    const [partialFulfillErrorCode, setPartialFulfillErrorCode] = useState(null)
    const [partialFulfillProgress, setPartialFulfillProgress] = useState({
        isComplete: false,
        failures: 0,
        total: 0,
        pending: 0,
        failInfo: []
    })
    const [partialResult, setPartialResult] = useState({ items: [], cancelOrder: false })
    const [tasksSummaryOrderId, setTasksSummaryOrderId] = useState(null)
    const {i18n} = useTranslation()
    const dispatch = useDispatch()
    const pageSize = useSelector((state) => state.pageSize.processingOrdersList)
    const defaultSearchParams = {
        page: 1,
        per_page: pageSize,
        status: ['processing', 'picking', 'packing'],
        order_by: 'max_packing_time',
        order: 'ASC',
        warehouse_id: isNaN(parseInt(warehouse_id)) ? null : parseInt(warehouse_id),
        pick_n_ship: false
    }

    const [searchParams, setSearchParams] = useState({...defaultSearchParams,  warehouse_id: warehouse_id})

    const additionalParams = {
        include_inactive: true
    }


    const table_columns = [
        {
            Header: i18n.t('list_processing_orders.headers.store'),
            accessor: 'store',
            disableFilters: false,
            disableSortBy: false,
            placeholder: i18n.t('list_processing_orders.store_search_placeholder'),
            inputHoverDescription: (
                <div>
                    {i18n.t('list_processing_orders.headers.store_search_help_1')}
                    <br/> <br/>
                    {i18n.t('list_processing_orders.headers.store_search_help_2')}
                </div>
            )
        },
        {
            Header: i18n.t('list_processing_orders.headers.order_number'),
            accessor: 'order_number', // accessor is the "key" in the data
            disableFilters: false,
            disableSortBy: false,
        },
        {
            Header: i18n.t('list_processing_orders.headers.max_packing_time'),
            accessor: 'max_packing_time',
            Filter: DateTimeFilter,
            disableFilters: false,
            disableSortBy: false,
            shrinkToContent: true
        },
        {
            Header: i18n.t('list_processing_orders.headers.channel_name'),
            accessor: 'channel_name',
            disableFilters: false,
            disableSortBy: true,
            Filter: SelectColumnFilter,
            selectFilterOptions: ['manual', 'shopee', 'meli', 'liverpool', 'walmart', 'vtex'].map((channel_type)=>{

                return {
                    id: channel_type,
                    label: channel_type
                }
            }),
        },
        {
            Header: i18n.t('list_processing_orders.headers.tote'),
            accessor: 'tote', // accessor is the "key" in the data
            disableFilters: false,
            disableSortBy: true,
            searchAs: 'tote_search',
        },
        {
            Header: i18n.t('list_processing_orders.headers.picker'),
            accessor: 'assigned_to', // accessor is the "key" in the data
            disableFilters: false,
            disableSortBy: true,
        },
        // {
        //     Header: 'REFERENCIA',
        //     accessor: '',
        //     disableFilters: true,
        //     disableSortBy: true
        // },
        {
            Header: i18n.t('list_processing_orders.headers.status'),
            accessor: 'status', // accessor is the "key" in the data
            Filter: SelectColumnMultiFilter,
            selectFilterOptions: ORDER_STATUSES_PROCESSING.filter((status)=>status.filterable !== false).map((status)=> {
                return status.filterable === false ? null : {id: status.status, label: status.label}
            }),
            disableFilters: false,
            disableSortBy: false,
        },
        ...(user?.permissions?.beta_features ? [{
            Header: i18n.t('list_processing_orders.headers.has_unassigned_pickable'),
            accessor: 'has_unassigned_pickable',
            disableFilters: false,
            disableSortBy: true,
            shrinkToContent: true,
            Filter: SelectColumnFilter,
            selectFilterOptions: [
                { id: 'true', label: i18n.t('list_processing_orders.has_unassigned_pickable.filter') },
            ],
        }] : []),
        // {
        //     Header: 'DESTINO (e-mail)',
        //     accessor: 'email',
        //     disableFilters: false,
        //     disableSortBy: true,
        // },
        // {
        //     Header: 'Fecha de Creación',
        //     accessor: 'channel_created_at', // accessor is the "key" in the data
        //     Filter: DateTimeRangeFilter,
        //     disableFilters: false,
        //     disableSortBy: false,
        //     shrinkToContent: true
        // },
        /*{
            Header: 'En pendiente desde',
            accessor: 'pending_at', // accessor is the "key" in the data
            Filter: DateTimeRangeFilter,
            disableFilters: false,
            disableSortBy: false,
            shrinkToContent: true
        },*/
        ...(user?.permissions?.beta_features ? [{
            Header: i18n.t('list_processing_orders.headers.pickability'),
            accessor: 'pickability_status',
            disableFilters: false,
            disableSortBy: true,
            shrinkToContent: true,
            Filter: SelectColumnMultiFilter,
            selectFilterOptions: PICKABILITY_STATUSES.map((s) => ({
                id: s.status,
                label: i18n.t(`pickability.status.${s.status}`),
            })),
        }] : []),
        {
            Header: "",
            accessor: 'actions', // accessor is the "key" in the data
            disableFilters: true,
            disableSortBy: true,
            fetchingIndicator : true,
            shrinkToContent: true
        },
    ]

    const {
        data: shippingMethodsData,
        isLoading: isLoadingShippingMethods ,
        isError: isErrorShippingMethods,
        error: errorShippingMethods,
        isFetching: isFetchingShippingMethods,
        isPreviousData: isPreviousDataShippingMethods,
        refetch: refetchShippingMethods
    } = useQuery(['shipping_methods', warehouse_id], () => fetchWarehouseShippingMethods(warehouse_id, additionalParams), {
        keepPreviousData : true,
        refetchOnWindowFocus: false
    })

    const {
        isLoading,
        isError,
        error,
        data,
        isFetching,
        isPreviousData,
        refetch,
    } = useQuery(['processing_orders', searchParams], () => fetchProcessingOrders(searchParams), { keepPreviousData : true })

        useEffect(() => {
            const fetchStoreConfigurations = async () => {
                if (!data?.orders) return

                const uniqueStoreIds = [...new Set(data.orders.map(order => order.store?.id).filter(Boolean))]
                const newConfigurations = {}

                for (const storeId of uniqueStoreIds) {
                    try {
                        const partialReplenishmentInReviewEnabled = await fetchStoreConfig(storeId, KEY_PARTIAL_FULFILLMENT_IN_REVIEW_ENABLED)
                        newConfigurations[storeId] = partialReplenishmentInReviewEnabled === true || partialReplenishmentInReviewEnabled?.value === true
                    } catch (error) {
                        console.error(`Error fetching config for store ${storeId}:`, error)
                        newConfigurations[storeId] = false
                    }
                }

                setPartialFulfillmentInReviewStoreConfigs(newConfigurations)
            }

            fetchStoreConfigurations()
        }, [data?.orders])

    const handlerOnClickStatus = (order) => {
        // setOrderClicked(order)
        // setOpenDialogErrors(!openDialogErrors)
        history.push(PROCESSING_ORDER_DETAIL.replace(':id', order.id).replace(':warehouse_id', warehouse_id))
    }

    const onSortChange = (orderBy) => {
        let newSearchParams = {...searchParams}
        tableRef.current?.resetSelection()
        tableRef.current?.resetPagination()
        if (orderBy.length > 0) {
            setSearchParams({
                ...newSearchParams,
                order_by: orderBy[0].id,
                order: orderBy[0].desc ? 'DESC' : 'ASC'
            })
        }
        else {
            if ('order_by' in newSearchParams) {
                newSearchParams.order_by = defaultSearchParams.order_by
                newSearchParams.order = defaultSearchParams.order
                setSearchParams(newSearchParams)
            }
        }
    }

    const onFilterChange = (activeFilters) => {
        let newSearchParams = cloneDeep(searchParams)
        memoizedColumns.forEach(col => {
            if (newSearchParams[col.searchAs || col.accessor]) {
                delete newSearchParams[col.searchAs || col.accessor]
            }
        })

        let filters = {}
        activeFilters.forEach(filter => {
            let value = filter.value
            if (filter.id === 'store') {
                let splitValues
                if (value.includes(',')) {
                    splitValues = value.split(',').map(item => item.trim()).filter(item => item !== '')
                } else {
                    splitValues = value.split(/\s+/).filter(item => item !== '')
                }
                let notStoreValues = splitValues.filter(v => v.startsWith('-')).map(v => v.slice(1))
                let storeValues = splitValues.filter(v => !v.startsWith('-'))
                value = storeValues
                filters['not_store_values'] = notStoreValues
            }

            // Convert requires_partial_fulfillment string to boolean
            if (filter.id === 'requires_partial_fulfillment') {
                value = value === 'true'
            }

            if (filter.id === 'has_unassigned_pickable') {
                value = value === 'true'
            }

            let column = memoizedColumns.find((col) => col.accessor === filter.id)
            filters[column.searchAs || column.accessor] = value
        })

        if (!('not_store_values' in filters) || filters.not_store_values.length === 0) {
            const {not_store_values, ...restParams} = newSearchParams
            newSearchParams = restParams
        }

        if (!('status' in filters) || filters.status.length === 0) {
            filters.status = []
            filters.status.push(...defaultSearchParams.status)
        }
        tableRef.current?.resetPagination()
        setSearchParams({...newSearchParams, ...filters, page:1})
    }

    const handlePickAllStock = (order) => {
        setPickAllStockOrder(order)
        setShowPickAllConfirmation(true)
    }

    const handleFulfillPartially = (order) => {
        setPartialFulfillOrder(order)
        setPartialFulfillState('confirmation')
        setShowPartialFulfillDialog(true)
    }

    const handleUndoInReview = async (order, payload) => {
        const pickingOrderId = order?.picking_order?.id
        const orderNumber = order?.order_number
        const storeName = order?.store?.name

        if (!pickingOrderId) {
            setUndoInReviewOrder(null)
            setUndoInReviewErrorMessage(i18n.t('list_processing_orders.undo_in_review.error_no_picking_order'))
            setUndoInReviewOrderInfo({ orderNumber, storeName })
            setShowUndoInReviewErrorDialog(true)
            return
        }

        setLoadingUndoInReview(pickingOrderId)
        try {
            await undoPickingOrderInReview(pickingOrderId, payload)
            setUndoInReviewOrder(null)
            // Invalidate and refetch the processing orders query to refresh the table
            await queryClient.invalidateQueries(['processing_orders'])
            await refetch()
            setLoadingUndoInReview(null)
        } catch (error) {
            setLoadingUndoInReview(null)
            // undo_in_review's typed refusals nest the code under `error`; its Pundit rescue
            // returns it at the top level instead.
            // Read the tarja flag from the same expression that decides whether its button is
            // rendered, so a message can never offer a partial shipment the supervisor cannot see.
            const canSendPartial = partialFulfillmentInReviewStoreConfigs[order?.store?.id] === true
            const errorCode = error?.response?.data?.error?.code || error?.response?.data?.code
            let errorMessage = i18n.t('list_processing_orders.undo_in_review.error_generic')

            // Handle specific error codes
            switch (errorCode) {
                case 'ORDER_MISSING_STOCK':
                    errorMessage = i18n.t('list_processing_orders.undo_in_review.error_missing_stock')
                    break
                case 'forbidden':
                    errorMessage = i18n.t('list_processing_orders.undo_in_review.error_forbidden')
                    break
                case 'not_in_review':
                    errorMessage = i18n.t('list_processing_orders.undo_in_review.error_not_in_review')
                    break
                case 'requires_partial_fulfillment':
                    errorMessage = i18n.t('list_processing_orders.undo_in_review.error_requires_partial_fulfillment')
                    break
                case 'picker_required':
                    errorMessage = i18n.t('list_processing_orders.undo_in_review.error_picker_required')
                    break
                case 'picker_not_found':
                    errorMessage = i18n.t('list_processing_orders.undo_in_review.error_picker_not_found')
                    break
                case 'picker_not_in_warehouse':
                    errorMessage = i18n.t('list_processing_orders.undo_in_review.error_picker_not_in_warehouse')
                    break
                case 'picker_cannot_pick':
                    errorMessage = i18n.t('list_processing_orders.undo_in_review.error_picker_cannot_pick')
                    break
                case 'has_holded_stock':
                    errorMessage = i18n.t(canSendPartial
                        ? 'list_processing_orders.undo_in_review.error_has_holded_stock'
                        : 'list_processing_orders.undo_in_review.error_has_holded_stock_no_partial')
                    break
                case 'has_expired_stock':
                    errorMessage = i18n.t(canSendPartial
                        ? 'list_processing_orders.undo_in_review.error_has_expired_stock'
                        : 'list_processing_orders.undo_in_review.error_has_expired_stock_no_partial')
                    break
                case 'order_interrupted':
                    errorMessage = i18n.t('list_processing_orders.undo_in_review.error_order_interrupted')
                    break
                case 'natura_packing_status':
                case 'in_packing_status':
                    errorMessage = i18n.t('list_processing_orders.undo_in_review.error_in_packing_status')
                    break
                default:
                    // Fallback to error message from API or generic error
                    errorMessage = error?.response?.data?.error?.message || error?.message || errorMessage
            }
            
            if (!RECOVERABLE_UNDO_ERRORS.includes(errorCode)) setUndoInReviewOrder(null)
            setUndoInReviewErrorMessage(errorMessage)
            setUndoInReviewOrderInfo({ orderNumber, storeName })
            setShowUndoInReviewErrorDialog(true)
            console.error('Error undoing in review:', error)
        }
    }

    const onCancelPartialFulfill = () => {
        setPartialFulfillOrder(null)
        setShowPartialFulfillDialog(false)
        setPartialFulfillState('confirmation')
        setPartialFulfillProgress({
            isComplete: false,
            failures: 0,
            total: 0,
            pending: 0,
            failInfo: []
        })
        setPartialFulfillErrorMessage('')
        setPartialFulfillErrorList([])
        setPartialFulfillErrorCode(null)
    }

    // Put the dialog into the error view for a refusal the supervisor can act on, instead of
    // the raw backend text with a retry that would fail the same way.
    const markPartialFulfillBlocked = (code) => {
        setPartialFulfillErrorCode(code)
        setPartialFulfillErrorMessage('')
        setPartialFulfillErrorList([])
    }

    const onConfirmPartialFulfill = async () => {
        setPartialFulfillState('loading')
        setPartialFulfillErrorCode(null)

        // No client-side fiscal pre-check: the backend is the source of truth (J2).
        // Gating on the list's cached order.invoices could hard-block on stale data
        // (e.g. after the NF-e is regenerated) with no override; instead we let the
        // request through and detect a fiscal block reactively (see the catch).
        try {
            const pickingOrderId = partialFulfillOrder?.picking_order?.id
            if (!pickingOrderId) {
                setPartialFulfillErrorMessage('Order data is incomplete. Please try again.')
                setPartialFulfillState('error')
                return
            }

            // B2B (picking-tasks) uses its dedicated endpoint; D2C uses the classic
            // in-review batch. Both return { batch_id } and share the tracker + dialog.
            const batch = partialFulfillOrder?.uses_picking_tasks
                ? await partialFulfillB2b(pickingOrderId)
                : await partialFulfillOrdersInReview([pickingOrderId])
            const result = await fetchPartialFulfillmentResult(batch.batch_id)
            setPartialResult({
                items: result.items || [],
                cancelOrder: !!result.cancelOrder,
            })
            setPartialFulfillState('completion')
            // CUBBO-2461 J6: refresh the list so the tarja'd row drops out of
            // getPartialFulfillMode and no longer offers a second "Enviar parcial"
            // (mirrors handleUndoInReview / onConfirmPickAllStock). Don't rely on
            // refetchOnWindowFocus — the operator can now dismiss via Esc/backdrop
            // without leaving the tab.
            await queryClient.invalidateQueries(['processing_orders'])
            await refetch()
        } catch (error) {
            // The refusals with somewhere to go get their own view; everything else falls
            // back to the backend text. Detection is defensive across the batch failInfo and the
            // HTTP envelope (see partialFulfillErrors.js).
            const errorCode = getPartialFulfillErrorCode(error)
            if (PARTIAL_FULFILL_BLOCKING_CODES.includes(errorCode)) {
                markPartialFulfillBlocked(errorCode)
            } else {
                // Normalize to a string — the envelope error can be a nested
                // { code, message } object; rendering it raw would crash React (J4).
                const list = error?.data?.failInfo || []
                setPartialFulfillErrorCode(null)
                setPartialFulfillErrorMessage(getPartialFulfillErrorMessage(error))
                setPartialFulfillErrorList(list)
            }
            setPartialFulfillState('error')
            console.error('Erro ao processar envio parcial:', error)
        }
    }

    const handleGoToGenerateLabels = () => {
        try {
            const orderId = partialFulfillOrder?.id
            if (!orderId) return
            const url = GENERATE_ORDER_LABEL.replace(':id', orderId).replace(':warehouse_id', warehouse_id)
            setShowPartialFulfillDialog(false)
            setPartialFulfillState('confirmation')
            setPartialFulfillOrder(null)
            window.open(url, '_blank', 'noopener,noreferrer')
        } catch (_) {
        } finally {
            setShowPartialFulfillDialog(false)
            setPartialFulfillState('confirmation')
            setPartialFulfillOrder(null)
        }
    }

    const onCancelPickAllStock = () => {
        setPickAllStockOrder(null)
        setShowPickAllConfirmation(false)
    }

    const onConfirmPickAllStock = async (order) => {
        setLoadingPickAllStock(true)
        try {
            await pickAllOrderStocks(order.id)
            await refetch()
            setPickAllStockOrder(null)
            setShowPickAllConfirmation(false)
            setShowPickAllSuccess(true)
        } catch (error) {
            window.alert(error.response.data?.error)
            console.log(error)
            console.log(error.response.data?.error)
        }
        setLoadingPickAllStock(false)
    }

    const getActions = (order) => {
        const canViewTasks = order.uses_picking_tasks && ['processing', 'picking', 'packing'].includes(order.status)
        const viewTasksButton = canViewTasks ? (
            <span key="view-tasks" className="text-indigo-600 hover:text-indigo-900 cursor-pointer inline-flex items-center gap-1" onClick={() => setTasksSummaryOrderId(order.id)}>
                {i18n.t('picking_tasks.actions.view_tasks')}
            </span>
        ) : null

        if (order.status == "packing") {
            // Generating the label ships every unit and releases the totes, and the
            // endpoint does not check in_review — so a quarantined lot would leave.
            const reviewBlock = getReviewBlockMessage({ ...order.picking_order, order })
            return (
                <div className="flex flex-col items-start gap-2">
                    {reviewBlock
                        ? <Tooltip title={i18n.t(reviewBlock.key, reviewBlock.params)}>
                            <span className="text-gray-400 cursor-not-allowed">{i18n.t('list_processing_orders.actions.generate_shipment')}</span>
                          </Tooltip>
                        : <Link  to={GENERATE_ORDER_LABEL.replace(":id", order.id).replace(":warehouse_id",warehouse_id)} className="text-indigo-600 hover:text-indigo-900">{i18n.t('list_processing_orders.actions.generate_shipment')}</Link>}
                    {viewTasksButton}
                </div>
            )
        }

        const actionButtons = []
        if (!order.uses_picking_tasks && user?.permissions?.pick_all_order_stocks && order.assigned_stock_quantity && order.assigned_stock_quantity >= 20) {
            actionButtons.push(
                <span key="pick-all" className="text-indigo-600 hover:text-indigo-900 cursor-pointer" onClick={() => handlePickAllStock(order)}>
                    {i18n.t('list_processing_orders.actions.pick_pieces')}
                </span>
            )
        }

        if (viewTasksButton) {
            actionButtons.push(viewTasksButton)
        }

        // "Enviar parcial" is shown for the classic in-review path and for picking-tasks orders
        // (parked under review, or flagged as a mandatory tarja) — see getPartialFulfillMode; both
        // sit behind the per-store flag. "Regresar a Picking" is deliberately NOT behind it: it
        // is the non-destructive way out, and gating it there stranded stores without the flag.
        const hasPermission = user?.permissions?.[PERMISSION_PARTIALLY_FULFILL_IN_REVIEW]
        const storeConfigEnabled = partialFulfillmentInReviewStoreConfigs[order.store?.id] === true
        const partialFulfillMode = getPartialFulfillMode(order, { hasPermission, storeConfigEnabled })

        if (partialFulfillMode) {
            actionButtons.push(
                <span key="partial" className="text-indigo-600 hover:text-indigo-900 cursor-pointer" onClick={() => handleFulfillPartially(order)}>
                    {i18n.t('list_processing_orders.actions.fulfill_partially')}
                </span>
            )
        }

        if (canUndoInReview(order, { hasReviewPermission: Boolean(user?.permissions?.put_picking_order_in_review) })) {
            const isLoading = loadingUndoInReview === order.picking_order?.id
            actionButtons.push(
                <span
                    key="undo-in-review"
                    className={`text-indigo-600 hover:text-indigo-900 cursor-pointer ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() => !isLoading && setUndoInReviewOrder(order)}
                >
                    {isLoading ? i18n.t('list_processing_orders.actions.undo_in_review_loading') : i18n.t('list_processing_orders.actions.undo_in_review')}
                </span>
            )
        }

        if (actionButtons.length === 0) return <></>
        return <div className="flex flex-col items-start gap-2">{actionButtons}</div>
    }

    const buildToteRow = function(picking_order) {
        if (!picking_order) return ''

        // in_review is a picking_order-level flag (not per-tote) — show it once.
        const inReviewTooltip = picking_order.in_review && (
            <Tooltip title="Tote en revisión"><ExclamationCircleIcon className="h-5 w-5 ml-1 text-red-400" /></Tooltip>
        )

        // Multi-task orders return every assigned tote in totes[]; legacy/single-picker
        // orders return [] and we fall back to the single tote.
        const totes = Array.isArray(picking_order.totes) && picking_order.totes.length > 0
            ? picking_order.totes
            : (picking_order.tote ? [picking_order.tote] : [])

        if (totes.length === 0) return ''

        return (
            <ExpandableList
                items={totes}
                renderItem={(tote, index) => (
                    <div key={tote.id} className="flex items-center">
                        {tote.unique_code}{index === 0 && inReviewTooltip}
                    </div>
                )}
            />
        )
    }

    const buildPickerCell = function(picking_order) {
        if (!picking_order) return ''

        // Multi-task orders return every assigned picker in pickers[]; legacy/single-picker
        // orders return [] and we fall back to the single assigned_to.
        const pickers = Array.isArray(picking_order.pickers) && picking_order.pickers.length > 0
            ? picking_order.pickers
            : (picking_order.assigned_to ? [picking_order.assigned_to] : [])

        if (pickers.length === 0) return ''

        return (
            <ExpandableList
                items={pickers}
                renderItem={(picker) => (
                    <span key={picker.id}>
                        {picker.first_name} {picker.last_name}
                        {picker.tasks_count ? <span className="text-gray-400"> ({picker.tasks_count})</span> : null}
                    </span>
                )}
            />
        )
    }

    const getTableData = () => {
        return data.orders.map((order)=>{
            if(order.store.name.length > 27) order.store.name = order.store.name.substring(0, 24) + '...'
            const requiresPartialFulfillment = order.picking_order?.requires_partial_fulfillment
            return {
                entity: {...order},
                store:<div className="">{String(order.store.name)}</div>,
                order_number: (
                    <div className="flex items-center gap-1">
                        <span className="hover:underline cursor-pointer" onClick={()=>handlerOnClickStatus(order)}>#{String(order.order_number)}</span>
                        {order.is_b2b && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">{i18n.t('picking_tasks.badge_b2b')}</span>}
                    </div>
                ),
                channel_name: order.channel_name,
                tote: buildToteRow(order.picking_order),
                picking_order: order.picking_order,
                assigned_to: buildPickerCell(order.picking_order),
                pickability_status: <PickabilityBadge pickability={order.pickability} orderNumber={order.order_number} warehouseId={warehouse_id} />,
                has_unassigned_pickable: order.pickability?.has_unassigned_pickable ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-800">
                        {i18n.t('list_processing_orders.has_unassigned_pickable.tag')}
                    </span>
                ) : null,
                status: <StatusPill className="cursor-pointer" status={order.status} statuses={ORDER_STATUSES_PROCESSING} onClick={()=>handlerOnClickStatus(order)}/>,
                shipping_method: formatShippingMethodFilterLabel(order.shipping_method),
                pending_at: <FormattedDate date={order.pending_at} shortDate />,
                max_packing_time: order.max_packing_time ? <FormattedRelativeTime date={order.max_packing_time} calendarTime/> : "",
                requires_partial_fulfillment: requiresPartialFulfillment ? (
                    <Tooltip title={i18n.t('list_processing_orders.requires_partial_fulfillment.tooltip')}>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                            {i18n.t('list_processing_orders.requires_partial_fulfillment.yes')}
                        </span>
                    </Tooltip>
                ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        {i18n.t('list_processing_orders.requires_partial_fulfillment.no')}
                    </span>
                ),
                actions: getActions(order)
            }
        })
    }

    const getColumns = () => {
        if(shippingMethodsData) {
            let newColumns = [...table_columns]
            let index = newColumns.findIndex(column => column.accessor === "shipping_method")
            if(index === -1) {
                newColumns.splice(3,0,{
                    Header: i18n.t('list_processing_orders.headers.shipping_method'),
                    accessor: 'shipping_method',
                    disableFilters: false,
                    disableSortBy: false,
                    // Keep the string accessor for filtering/sorting; render the
                    // carrier with its internal-carrier tooltip from the original
                    // order object (CUBBO-2185).
                    Cell: ({ row }) => {
                        const sm = row.original.entity?.shipping_method
                        if (!sm) return '-'
                        return <CarrierName carrierName={sm.carrier_name} internalCarrierName={sm.internal_carrier_name} suffix={sm.shipping_name} />
                    },
                    Filter: SelectColumnFilter,
                    selectFilterOptions:shippingMethodsData.map((ship)=> {
                        return {
                            id: formatShippingMethodFilterId(ship),
                            label: formatShippingMethodFilterLabel(ship)
                        }
                    })
                })
            }
            // Add requires_partial_fulfillment column if user has permission
            if (user?.permissions?.[PERMISSION_PARTIALLY_FULFILL_IN_REVIEW]) {
                const requiresPartialIndex = newColumns.findIndex(column => column.accessor === "requires_partial_fulfillment")
                if (requiresPartialIndex === -1) {
                    const toteIndex = newColumns.findIndex(column => column.accessor === "tote")
                    newColumns.splice(toteIndex + 1, 0, {
                        Header: i18n.t('list_processing_orders.headers.requires_partial_fulfillment'),
                        accessor: 'requires_partial_fulfillment',
                        disableFilters: false,
                        disableSortBy: true,
                        Filter: SelectColumnFilter,
                        selectFilterOptions: [
                            { id: 'true', label: i18n.t('list_processing_orders.requires_partial_fulfillment.yes') },
                            { id: 'false', label: i18n.t('list_processing_orders.requires_partial_fulfillment.no') },
                        ],
                    })
                }
            }
            return newColumns
        }
        return []
    }

    const [selectedOrders, setSelectedOrders] = useState([])
    const [showAssignOrderDialog, setShowAssignOrderDialog] = useState(false)
    const prepareModalData = (picking_order) => {
        setShowAssignOrderDialog(true)
    }

    // REM / B2B orders use the picking-tasks system (uses_picking_tasks) and are reassigned
    // per task in the tasks modal. order_type isn't reliably present in the processing list,
    // so uses_picking_tasks is the signal that distinguishes them from regular orders.
    const isTaskReassignableOrder = (entity) => !!entity?.uses_picking_tasks

    const anySelectedInReview = () => selectedOrders.some(x => isOrderInReview(x.entity))

    const canOrdersBeReassigned  = () => {
        // An order under review owns no reassignable work until a supervisor resolves it: the
        // legacy path has always refused, and assign-tasks-bulk now answers 422 order_in_review.
        if (anySelectedInReview()) return false
        // Picking-task orders (REM / B2B) are reassigned per task in the tasks modal, which
        // only supports one order at a time — allow it only for a single such order.
        if (selectedOrders.some(x => isTaskReassignableOrder(x.entity))) return selectedOrders.length === 1
        return selectedOrders.length == selectedOrders.filter(x => x.status.props.status == 'processing' || x.status.props.status == 'picking').length
    }

    const onAssignOrders = () => {
        const single = selectedOrders[0]
        if (selectedOrders.length === 1 && isTaskReassignableOrder(single?.entity)) {
            setTasksSummaryOrderId(single.entity.id)
            return
        }
        setShowAssignOrderDialog(true)
    }

    const onOrdersAssigned = () => {
        setShowAssignOrderDialog(false)
        // Keep the column filters: the table still shows what the user typed, so
        // resetting the params left the inputs filled over an unfiltered list.
        setSearchParams(prev => ({...prev, page: 1}))
        refetch()
        tableRef.current?.resetSelection()
        tableRef.current?.resetPagination()
    }

    const memoizedColumns = useMemo(() => getColumns(), [shippingMethodsData, user])

    const tableRef = useRef()

    return (
        <>
           <PageView
                topMenu={<PageTopBar>
                    <div className="text-lg font-semibold">{i18n.t('list_processing_orders.title')}</div>
                    <>
                        <Button 
                            type="secondary" 
                            onClick={() => history.push(PICKING_PRODUCTIVITIES.replace(':warehouse_id', warehouse_id))}
                            className="whitespace-nowrap mr-2"
                        >
                            Productividades de Picking
                        </Button>
                        {user?.permissions?.reassign_picking_order && (
                            canOrdersBeReassigned() ?
                                <Button className="whitespace-nowrap mr-2" onClick={onAssignOrders} disabled={selectedOrders.length < 1}>
                                        {`${i18n.t('list_processing_orders.reassign_picker')} (${selectedOrders.length})`}
                                </Button>
                                :
                                <Button className="whitespace-nowrap mr-2 has-tooltip opacity-50">
                                    <Tooltip title={anySelectedInReview()
                                        ? i18n.t('list_processing_orders.reassign_picker_in_review_tooltip')
                                        : i18n.t('list_processing_orders.reassign_picker_tooltip')}>
                                       {i18n.t('list_processing_orders.reassign_picker')} <ExclamationCircleIcon className="text-white w-4 h-4 inline" />
                                    </Tooltip>
                                </Button>
                        )}
                        <Button className="whitespace-nowrap mr-2" onClick={()=>{
                            history.push(GENERATE_LABELS.replace(':warehouse_id', warehouse_id))
                            }
                        }>
                            {i18n.t('list_processing_orders.generate_shipments')}
                        </Button>

                        { WAREHOUSES_WITH_SHARED_LABELS.includes(Number(warehouse_id)) && (
                        <Button className="whitespace-nowrap mr-2" onClick={()=>{
                            history.push(GENERATE_SHARED_LABEL.replace(':warehouse_id', warehouse_id))
                            }
                        }>
                            {i18n.t('list_processing_orders.generate_shared_shipment')}
                        </Button>
                        )}
                    </>
                </PageTopBar>}
                childrenFullWidth={true}
                topMenuFullWidth={true}
                //bottomMenu={<Button>Crear Orden</Button>}
            >

                {(!isPreviousData && isLoading) || (!isPreviousDataShippingMethods && isLoadingShippingMethods) ? (
                    <><Loader show={true}></Loader></>
                ) : isError ? (
                    <>Error: {error.message}</>
                ) : isErrorShippingMethods ? (
                    <>Error: {errorShippingMethods.message}</>
                ):(
                        <>
                             <BasicTable
                        showHeader
                        showLoader={isFetching && isPreviousData}
                        columns={memoizedColumns}
                        filterable
                        sortable
                        showPaginator
                        selectable
                        data = {getTableData()}
                        onFilterChange={(activeFilters) => onFilterChange(activeFilters)}
                        onSortChange={(activeSort) => onSortChange(activeSort)}
                        paginationMeta={data?.meta}
                        labelToPaginator={i18n.t('list_processing_orders.orders')}
                        pagesSize = {searchParams.per_page}
                        onPaginationChange= { (requestedPage) => { setSearchParams({...searchParams, page: requestedPage}) } }
                        onPageSizeChange= { (pageSize) => {
                            setSearchParams({...searchParams, per_page: pageSize, page: 1})
                            dispatch(setProcessingOrdersListPageSize(pageSize))
                            tableRef.current?.resetPagination()
                        }}
                        onSelectionChange={(rows) => {
                            setSelectedOrders(rows)
                            // console.log("rows", rows)
                        }}
                        showDataStatus
                        isFetching={isFetching}
                        emptyTableText={i18n.t('list_processing_orders.empty_table_text')}
                        ref={tableRef}
                        />
                        </>

                    )
                }
            </PageView>
            {user?.permissions?.reassign_picking_order &&
                <AssignOrdersDialog open={showAssignOrderDialog} orders={selectedOrders} changingPicker={true} warehouseId={warehouse_id} onCancel={() => setShowAssignOrderDialog(false)} onSuccess={() => onOrdersAssigned()} />
            }
            <Notification
                show={showPickAllSuccess}
                setShow={setShowPickAllSuccess}
                type="success"
                title={i18n.t('list_processing_orders.pick_all_success')}
            />
            <ConfirmDialog
                open={showPickAllConfirmation}
                setOpen={setShowPickAllConfirmation}
                title={`Confirma pickear piezas de #${pickAllStockOrder?.order_number}`}
                description={`Confirma que deseas pickear todas las ${pickAllStockOrder?.assigned_stock_quantity} piezas de este pedido.`}
                confirmLabel="Confirmar"
                cancelLabel="Cancelar"
                onConfirm={() => onConfirmPickAllStock(pickAllStockOrder)}
                onCancel={() => onCancelPickAllStock()}
                loading={loadingPickAllStock}
            />
            <DialogView
                open={!!tasksSummaryOrderId}
                setOpen={() => setTasksSummaryOrderId(null)}
                className="justify-items-start sm:max-w-4xl"
            >
                {tasksSummaryOrderId && (
                    <PickingTasksSummary
                        orderId={tasksSummaryOrderId}
                        warehouseId={warehouse_id}
                        onClose={() => setTasksSummaryOrderId(null)}
                    />
                )}
            </DialogView>
            <PartialFulfillDialog
                open={showPartialFulfillDialog}
                state={partialFulfillState}
                order={partialFulfillOrder}
                result={partialResult}
                errorMessage={partialFulfillErrorMessage}
                errorList={partialFulfillErrorList}
                errorCode={partialFulfillErrorCode}
                onCancel={onCancelPartialFulfill}
                onConfirm={onConfirmPartialFulfill}
                onGoToGenerateLabels={handleGoToGenerateLabels}
            />
            {undoInReviewOrder && (
                <UndoInReviewDialog
                    open={!!undoInReviewOrder}
                    order={undoInReviewOrder}
                    warehouseId={warehouse_id}
                    submitting={loadingUndoInReview === undoInReviewOrder?.picking_order?.id}
                    onCancel={() => setUndoInReviewOrder(null)}
                    onConfirm={(payload) => handleUndoInReview(undoInReviewOrder, payload)}
                />
            )}
            {showUndoInReviewErrorDialog && (
                <DialogView open={showUndoInReviewErrorDialog} setOpen={setShowUndoInReviewErrorDialog}>
                    <div className="text-center">
                        <div className="mb-4">
                            <svg className="w-16 h-16 mx-auto text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {i18n.t('list_processing_orders.undo_in_review.error_title')}
                        </h3>
                        {(undoInReviewOrderInfo.orderNumber || undoInReviewOrderInfo.storeName) && (
                            <div className="mb-4 flex flex-wrap gap-2 justify-center">
                                {undoInReviewOrderInfo.orderNumber && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        #{undoInReviewOrderInfo.orderNumber}
                                    </span>
                                )}
                                {undoInReviewOrderInfo.storeName && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                        {undoInReviewOrderInfo.storeName}
                                    </span>
                                )}
                            </div>
                        )}
                        <p className="text-gray-500 mb-6">
                            {undoInReviewErrorMessage}
                        </p>
                        <div className="flex gap-3 justify-center">
                            <Button onClick={() => setShowUndoInReviewErrorDialog(false)}>
                                {i18n.t('list_processing_orders.undo_in_review.error_ok_label')}
                            </Button>
                        </div>
                    </div>
                </DialogView>
            )}
            <Switch>
                <Route exact path={PROCESSING_ORDER_DETAIL}>
                    <SlidePanel title={i18n.t('orders.order_details.title')} referrer={ORDERS_PROCESSING.replace(':warehouse_id', warehouse_id)}>
                        <ShowOrderContainer />
                    </SlidePanel>
                </Route>
            </Switch>
        </>
    )
}
